/**
 * WorkLogin — Personalnummer + PIN Login for work.weship.eu / /work route.
 *
 * Designed for ~30 non-tech-savvy warehouse workers:
 * - Large fonts (18px+ body, 24px+ inputs)
 * - Large touch targets (56px+ buttons)
 * - Simple language, minimal text
 * - WS- prefix fixed, user types only digits
 * - Hint: "Nummer und PIN stehen auf deinem Mitarbeiterausweis"
 *
 * @page /work (unauthentifiziert)
 * @roles keine (öffentlich)
 * @endpoints POST /api/portal/login-personalnummer
 * @since v2.296.0
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

export default function WorkLogin() {
  const { loginPersonalnummer } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [step, setStep] = useState('nummer'); // nummer | pin | forgot
  const [nummer, setNummer] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotNummer, setForgotNummer] = useState('');
  const nummerRef = useRef(null);

  useEffect(() => {
    // Set work mode flag
    sessionStorage.setItem('weship_work_mode', 'true');
  }, []);

  useEffect(() => {
    if (step === 'nummer' && nummerRef.current) {
      nummerRef.current.focus();
    }
  }, [step]);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleNummerSubmit = (e) => {
    e?.preventDefault();
    if (!nummer.trim()) return;
    setError('');
    setStep('pin');
    setPin('');
  };

  const handlePinDigit = (digit) => {
    if (pin.length >= 4) return;
    const newPin = pin + digit;
    setPin(newPin);
    if (newPin.length === 4) {
      submitLogin(newPin);
    }
  };

  const handlePinBackspace = () => {
    setPin(p => p.slice(0, -1));
    setError('');
  };

  const submitLogin = async (pinVal) => {
    setError('');
    setLoading(true);
    try {
      const pnr = `WS-${nummer.padStart(4, '0')}`;
      await loginPersonalnummer(pnr, pinVal);
      navigate('/portal', { replace: true });
    } catch (err) {
      setError(err.message);
      setPin('');
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const handlePinForgot = async () => {
    const pnr = forgotNummer || nummer;
    if (!pnr.trim()) { setForgotError(t('work.login.enterNummer', 'Bitte Personalnummer eingeben')); return; }
    setLoading(true);
    setForgotError('');
    setForgotMsg('');
    try {
      const fullPnr = `WS-${pnr.padStart(4, '0')}`;
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/portal/pin-vergessen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personalnummer: fullPnr }),
      });
      const data = await res.json();
      if (res.ok) {
        setForgotMsg(data.message || t('work.login.pinSent', 'Ein neuer PIN wurde an deine Handynummer gesendet.'));
      } else {
        if (data.not_verified) {
          setForgotError(t('work.login.notVerified', 'Telefonnummer nicht verifiziert. Bitte frag deinen Teamleiter.'));
        } else {
          setForgotError(data.error || t('work.login.forgotError', 'Fehler'));
        }
      }
    } catch {
      setForgotError(t('work.login.forgotError', 'Fehler'));
    } finally {
      setLoading(false);
    }
  };

  const NumpadButton = ({ value, onClick, children, danger }) => (
    <button
      type="button"
      onClick={onClick || (() => handlePinDigit(value))}
      disabled={loading}
      className={`
        flex items-center justify-center rounded-2xl font-semibold
        transition-all active:scale-95 select-none
        ${danger
          ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
          : 'bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-inset,#F8F9FC)] active:bg-[var(--bg-inset,#F0F1F5)]'
        }
        disabled:opacity-40 disabled:cursor-not-allowed
      `}
      style={{ height: 68, minWidth: 68, fontSize: 28 }}
    >
      {children || value}
    </button>
  );

  return (
    <div className="min-h-[100dvh] bg-[var(--bg-page,#F8F9FC)] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-[340px]">
        {/* Logo */}
        <div className="flex flex-col items-center mb-6">
          <img
            src={theme === 'dark' ? '/weship-logo-light.png' : '/weship-logo.png'}
            alt="WeShip"
            className="h-12 mb-4"
          />
          <h1 className="text-2xl font-bold text-[var(--text-primary,#1A1A2E)]">
            {t('work.login.title', 'Willkommen bei WeShip!')}
          </h1>
        </div>

        {step === 'nummer' ? (
          /* ── Step 1: Personalnummer ── */
          <form onSubmit={handleNummerSubmit} className="space-y-5">
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl p-6 space-y-4">
              <label className="block text-lg font-medium text-[var(--text-secondary,#4B5563)]">
                {t('work.login.nummerLabel', 'Deine Mitarbeiter-Nummer:')}
              </label>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-[var(--text-tertiary,#6B7280)]">WS-</span>
                <input
                  ref={nummerRef}
                  type="text"
                  inputMode="numeric"
                  value={nummer}
                  onChange={(e) => setNummer(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className="flex-1 min-w-0 bg-[var(--bg-input,#F8F9FC)] border border-[var(--border-input,#E5E7EB)] rounded-xl px-4 text-2xl text-center font-mono font-bold text-[var(--text-primary,#1A1A2E)] placeholder-[var(--text-muted,#9CA3AF)] focus:outline-none focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20"
                  style={{ height: 56 }}
                  placeholder="0067"
                  autoComplete="off"
                />
              </div>
            </div>

            {error && (
              <p className="text-center text-lg font-semibold text-red-500 px-4">{error}</p>
            )}

            <button
              type="submit"
              disabled={!nummer.trim()}
              className="w-full bg-[#338edc] hover:bg-[#2b7bc4] disabled:bg-[#338edc]/30 disabled:cursor-not-allowed text-white font-bold text-lg rounded-2xl transition-colors"
              style={{ height: 56 }}
            >
              {t('work.login.weiter', 'Weiter')}
            </button>

            <p className="text-center text-base text-[var(--text-tertiary,#6B7280)] px-2">
              {t('work.login.hint', 'Nummer und PIN stehen auf deinem Mitarbeiterausweis.')}
            </p>
          </form>
        ) : (
          /* ── Step 2: PIN ── */
          <div className="space-y-5">
            {/* Back + MA Number */}
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => { setStep('nummer'); setPin(''); setError(''); }}
                className="w-12 h-12 rounded-xl flex items-center justify-center bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)]"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <div>
                <div className="text-sm text-[var(--text-tertiary,#6B7280)]">{t('work.login.nummerLabel', 'Deine Mitarbeiter-Nummer:')}</div>
                <div className="text-xl font-bold font-mono text-[var(--text-primary,#1A1A2E)]">WS-{nummer.padStart(4, '0')}</div>
              </div>
            </div>

            {/* PIN Dots */}
            <div className={`bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl p-6 ${shake ? 'animate-[shake_0.5s_ease-in-out]' : ''}`}>
              <p className="text-lg text-center font-medium text-[var(--text-secondary,#4B5563)] mb-5">
                {t('work.login.pinLabel', 'Dein PIN:')}
              </p>
              <div className="flex justify-center gap-5 mb-3">
                {[0, 1, 2, 3].map(i => (
                  <div
                    key={i}
                    className={`w-5 h-5 rounded-full transition-all duration-200 ${
                      i < pin.length
                        ? 'bg-[#338edc] scale-110'
                        : 'bg-[var(--border-card,#E5E7EB)]'
                    }`}
                  />
                ))}
              </div>
              {error && (
                <p className="text-center text-lg font-semibold text-red-500 mt-4">{error}</p>
              )}
            </div>

            {/* Numpad */}
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                <NumpadButton key={n} value={String(n)} />
              ))}
              <div /> {/* empty cell */}
              <NumpadButton value="0" />
              <NumpadButton onClick={handlePinBackspace}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 4H8l-7 8 7 8h13a2 2 0 002-2V6a2 2 0 00-2-2z" /><line x1="18" y1="9" x2="12" y2="15" /><line x1="12" y1="9" x2="18" y2="15" />
                </svg>
              </NumpadButton>
            </div>

            {loading && (
              <div className="flex justify-center py-2">
                <div className="animate-spin h-8 w-8 border-3 border-[#338edc] border-t-transparent rounded-full" />
              </div>
            )}

            <button
              type="button"
              onClick={() => { setStep('forgot'); setForgotNummer(nummer); setForgotMsg(''); setForgotError(''); }}
              className="w-full text-center text-base text-[#338edc] font-medium active:opacity-70"
            >
              {t('work.login.pinForgotLink', 'PIN vergessen?')}
            </button>
          </div>
        )}

        {step === 'forgot' && (
          /* ── Step 3: PIN vergessen ── */
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => { setStep(nummer ? 'pin' : 'nummer'); setForgotMsg(''); setForgotError(''); }}
                className="w-12 h-12 rounded-xl flex items-center justify-center bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)]"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <h2 className="text-lg font-bold text-[var(--text-primary,#1A1A2E)]">
                {t('work.login.pinForgotTitle', 'PIN vergessen')}
              </h2>
            </div>

            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl p-6 space-y-4">
              <p className="text-sm text-[var(--text-secondary,#4B5563)]">
                {t('work.login.pinForgotDesc', 'Gib deine Personalnummer ein. Wir senden dir einen neuen PIN per SMS.')}
              </p>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-[var(--text-tertiary,#6B7280)]">WS-</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={forgotNummer}
                  onChange={(e) => setForgotNummer(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className="flex-1 bg-[var(--bg-input,#F8F9FC)] border border-[var(--border-input,#E5E7EB)] rounded-xl px-4 text-2xl text-center font-mono font-bold text-[var(--text-primary,#1A1A2E)] placeholder-[var(--text-muted,#9CA3AF)] focus:outline-none focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20"
                  style={{ height: 56 }}
                  placeholder="0036"
                  autoComplete="off"
                />
              </div>
            </div>

            {forgotMsg && (
              <div className="bg-green-50 border border-green-200 rounded-2xl px-4 py-3">
                <p className="text-sm text-green-700 text-center">{forgotMsg}</p>
              </div>
            )}
            {forgotError && (
              <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
                <p className="text-sm text-red-600 text-center">{forgotError}</p>
              </div>
            )}

            <button
              type="button"
              onClick={handlePinForgot}
              disabled={loading || !forgotNummer.trim()}
              className="w-full bg-[#338edc] hover:bg-[#2b7bc4] disabled:bg-[#338edc]/30 disabled:cursor-not-allowed text-white font-bold text-lg rounded-2xl transition-colors"
              style={{ height: 56 }}
            >
              {loading
                ? t('work.login.sending', 'Wird gesendet...')
                : t('work.login.sendNewPin', 'Neuen PIN per SMS senden')
              }
            </button>

            {forgotMsg && (
              <button
                type="button"
                onClick={() => { setStep('nummer'); setPin(''); setError(''); }}
                className="w-full text-center text-base text-[#338edc] font-medium"
              >
                {t('work.login.backToLogin', 'Zurück zum Login')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}
